// Adapted from '@atlassian/jira-issue-table/src/common/fields/issue-key';

import React, { memo } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { useIntl } from '@atlassian/jira-intl';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../../controllers/features/selectors.tsx';
import type { ResolutionCell$key } from './__generated__/ResolutionCell.graphql';
import messages from './messages.tsx';

type Props = {
	fieldRef: ResolutionCell$key;
	isReadOnlyWithoutHoverPopover?: boolean;
};

export const ResolutionCell = memo<Props>(({ fieldRef, isReadOnlyWithoutHoverPopover }) => {
	const { formatMessage } = useIntl();
	const data = useFragment<ResolutionCell$key>(
		graphql`
			fragment ResolutionCell on JiraResolutionField {
				id
				resolution {
					name
				}
			}
		`,
		fieldRef,
	);

	const { resolution } = data;

	const isSingleLineRowHeightEnabled =
		useIsSingleLineRowHeightEnabled() && !isReadOnlyWithoutHoverPopover;

	const hoverPopoverRefFactory = isSingleLineRowHeightEnabled
		? // eslint-disable-next-line  react-hooks/rules-of-hooks
			useHoverPopoverContentRef(data.id)
		: null;

	const text = resolution?.name ?? formatMessage(messages.unresolved);

	if (!isSingleLineRowHeightEnabled)
		return <Cell data-vc="native-issue-table-ui-resolution-cell">{text}</Cell>;

	let hoverPopoverRef: HoverPopoverContentRef = null;
	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: text,
		});
	}

	return (
		<Cell data-vc="native-issue-table-ui-resolution-cell" ref={hoverPopoverRef}>
			{text}
		</Cell>
	);
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Cell = styled.div({
	overflowX: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});
